import { useCallback, useEffect, useState } from "react";
import { createInternalClient } from "@connect/data/internal/client";
import { log } from "@connect/observability";
import { runWithTransientRetry } from "./lib/retry-backoff";

export interface CountryState {
  code: string;
  name: string;
}

export interface Country {
  code: string;
  name: string;
  states: CountryState[];
}

const COUNTRY_NAMES = new Intl.DisplayNames(["en"], { type: "region" });

export function countryName(countries: Country[] | null, code: string): string {
  return (
    countries?.find((country) => country.code === code)?.name ?? COUNTRY_NAMES.of(code) ?? code
  );
}

interface CountriesAttempt {
  status: number;
  countries?: Country[];
}

async function requestCountries(
  client: ReturnType<typeof createInternalClient>,
): Promise<CountriesAttempt> {
  const result = await client.GET("/v3/countries").catch(() => null);
  if (!result) {
    return { status: 0 };
  }

  if (!Array.isArray(result.data)) {
    return { status: result.response.status };
  }

  const countries = result.data
    .map((country) => ({ code: country.alpha2, name: country.name, states: country.states ?? [] }))
    .sort((a, b) => a.name.localeCompare(b.name));

  return { status: result.response.status, countries };
}

function isTransient({ status, countries }: CountriesAttempt): boolean {
  return !countries && (status === 0 || status === 429 || status >= 500);
}

async function fetchCountries(
  apiBaseUrl: string,
  clientHeaders: Record<string, string>,
): Promise<Country[] | null> {
  const client = createInternalClient({ baseUrl: apiBaseUrl, headers: clientHeaders });

  const attempt = await runWithTransientRetry(() => requestCountries(client), isTransient);
  if (!attempt.countries) {
    log.warn("connect.onboarding.countries_failed", { status: attempt.status });
    return null;
  }

  return attempt.countries;
}

export function useCountryList(apiBaseUrl: string, clientHeaders: Record<string, string>) {
  const [countries, setCountries] = useState<Country[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;

    void fetchCountries(apiBaseUrl, clientHeaders).then((result) => {
      if (!cancelled) {
        setCountries(result);
        setFailed(result === null);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl, clientHeaders, attempt]);

  const reload = useCallback(() => {
    if (failed) {
      setFailed(false);
      setAttempt((current) => current + 1);
    }
  }, [failed]);

  return { countries, reload };
}
